import type { TaskNode, PlanData } from "./types";

export function fmt(n: number): string {
  return (Math.round(n * 100) / 100).toString();
}

// a "nice" axis step (1,2,2.5,5,10 × power of ten) at least span/count
export function niceStep(span: number, count: number): number {
  const raw = span / count;
  const pow = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * pow);
  return candidates.find((c) => c >= raw) || pow * 10 || 1;
}

export function slug(name: string): string {
  return (name || "plan").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "plan";
}

// "just now" / "5 min ago" / "3 days ago" — `now` is passed in so this stays pure/testable.
// Returns "" for a missing or unparseable stamp (callers hide the line).
export function relTime(iso: string | null | undefined, now: number): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const secs = Math.round((now - t) / 1000);
  if (secs < 0) return "just now"; // clock skew — don't say "in 3 minutes"
  const units: [number, string][] = [
    [60, "sec"], [3600, "min"], [86400, "hour"], [2592000, "day"], [31536000, "month"],
  ];
  if (secs < 45) return "just now";
  for (let i = 0; i < units.length; i++) {
    const [limit, name] = units[i];
    if (secs < limit) {
      const div = i === 0 ? 1 : units[i - 1][0];
      const n = Math.floor(secs / div);
      return n + " " + name + (n === 1 ? "" : "s") + " ago";
    }
  }
  const years = Math.floor(secs / 31536000);
  return years + " year" + (years === 1 ? "" : "s") + " ago";
}

export interface ExportCtx {
  projectName: string;
  mode: "client" | "server";
  serverVersion: number;
  changes: number;
  lastModified: string | null;
  showTags: boolean;
  showVersions: boolean;
  showComponents: boolean;
  showDone: boolean;
  showJira: boolean;
  showGantt: boolean;
  jiraUrl: string;
  jiraDelim: string;
  countDeps: boolean;
  depDocOrder: boolean;
  showComplexity: boolean;
  estimations: string[];
  estimateView: string;
  defaultEstimate: number;
  versions: string[];
  components: string[];
  tree: TaskNode[];
  now?: string; // ISO timestamp fallback when lastModified is null (injected for tests)
}

// the JSON export document: name, version (server only), changes, lastModified, the show* flags,
// the defined versions/components lists (so a copy-paste import restores them — no stale "(?)")
// and the tree.
export function exportDoc(ctx: ExportCtx): Partial<PlanData> & { version?: number } {
  const doc: any = { name: ctx.projectName };
  if (ctx.mode === "server") doc.version = ctx.serverVersion;
  doc.changes = ctx.changes;
  doc.lastModified = ctx.lastModified || ctx.now || new Date().toISOString();
  doc.showTags = ctx.showTags;
  doc.showVersions = ctx.showVersions;
  doc.showComponents = ctx.showComponents;
  doc.showDone = ctx.showDone;
  doc.showJira = ctx.showJira;
  doc.showGantt = ctx.showGantt;
  doc.jiraUrl = ctx.jiraUrl;
  doc.jiraDelim = ctx.jiraDelim;
  doc.countDeps = ctx.countDeps;
  doc.depDocOrder = ctx.depDocOrder;
  doc.showComplexity = ctx.showComplexity;
  doc.estimations = ctx.estimations;
  doc.estimateView = ctx.estimateView;
  doc.defaultEstimate = ctx.defaultEstimate;
  doc.versions = ctx.versions;
  doc.components = ctx.components;
  doc.tree = ctx.tree;
  return doc;
}

// filename: <project>__v<version>__c<changes>__<date>.json (version only in server mode)
export function exportFilename(ctx: Pick<ExportCtx, "projectName" | "mode" | "serverVersion" | "changes" | "lastModified" | "now">): string {
  const stamp = (ctx.lastModified || ctx.now || new Date().toISOString())
    .slice(0, 19).replace(/:/g, "-").replace("T", "_");
  const parts: string[] = [slug(ctx.projectName)];
  if (ctx.mode === "server") parts.push("v" + ctx.serverVersion);
  parts.push("c" + ctx.changes, stamp);
  return parts.join("__") + ".json";
}
